import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function ExportDocumentationPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const documents = [
    {
      icon: 'ri-file-text-line',
      title: 'Commercial Invoice',
      description: 'Detailed invoice preparation with accurate product descriptions, quantities, and values for customs clearance'
    },
    {
      icon: 'ri-file-list-line',
      title: 'Packing List',
      description: 'Complete packing details including dimensions, weights, and contents for shipping and customs'
    },
    {
      icon: 'ri-ship-line',
      title: 'Bill of Lading',
      description: 'Ocean and air cargo documentation ensuring proper shipment tracking and delivery confirmation'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Certificate of Origin',
      description: 'Official certification of product origin required for preferential trade agreements and customs'
    },
    {
      icon: 'ri-hospital-line',
      title: 'Phytosanitary Certificate',
      description: 'Plant health certification ensuring agricultural products meet importing country standards'
    },
    {
      icon: 'ri-check-double-line',
      title: 'Quality Certificates',
      description: 'FSSAI, ISO, and other quality certifications proving product standards and safety compliance'
    },
    {
      icon: 'ri-file-shield-line',
      title: 'Export License',
      description: 'Government authorization documentation for legal export of controlled commodities'
    },
    {
      icon: 'ri-building-line',
      title: 'Chamber Documents',
      description: 'Chamber of Commerce attestation and legalization for international trade recognition'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Requirement Analysis',
      description: 'We analyze your shipment details and destination country requirements to determine all necessary documentation'
    },
    {
      step: '02',
      title: 'Document Preparation',
      description: 'Our experts prepare all required documents with accurate information and proper formatting'
    },
    {
      step: '03',
      title: 'Verification & Attestation',
      description: 'Documents are verified for accuracy and attested by relevant authorities and chambers'
    },
    {
      step: '04',
      title: 'Submission & Tracking',
      description: 'We submit documents to customs and track the clearance process until successful completion'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="https://static.readdy.ai/image/d9bb5601c98f22586a2b11870925187d/b857441a8282eff6e67d45b8b6cd5de0.jpeg" alt="Horizon Pathways Logo" className="w-12 h-12 object-contain" />
              <span className="text-xl font-semibold text-gray-900">Horizon Pathways</span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Home</Link>
              <Link to="/products" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Products</Link>
              <a href="#services" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Services</a>
              <a href="#about" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">About</a>
              <a href="#contact" className="text-sm font-medium text-gray-700 transition-colors hover:text-emerald-600 whitespace-nowrap">Contact</a>
            </div>
            <button className="px-6 py-2 border-2 border-emerald-600 text-emerald-600 rounded-full text-sm font-medium transition-all hover:bg-emerald-600 hover:text-white whitespace-nowrap">Get Quote</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-8 relative overflow-hidden" style={{ backgroundColor: '#F8F6F3' }}>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-600 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full mb-6">
            <i className="ri-file-text-line text-emerald-700"></i>
            <span className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">Export Documentation Services</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6">
            Comprehensive Export <br />
            <span className="italic font-serif text-emerald-700">Documentation Support</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed mb-10">
            Navigate the complexities of international trade with our complete documentation services. We handle all paperwork, certifications, and compliance requirements so you can focus on growing your business.
          </p>
          <div className="flex items-center justify-center gap-4">
            <button className="px-8 py-4 bg-emerald-600 text-white rounded-full text-base font-semibold hover:bg-emerald-700 transition-all whitespace-nowrap">
              Request Documentation Support
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button className="px-8 py-4 border-2 border-gray-900 text-gray-900 rounded-full text-base font-semibold hover:bg-gray-900 hover:text-white transition-all whitespace-nowrap">
              Download Checklist
              <i className="ri-download-line ml-2"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Documents Grid */}
      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">COMPLETE DOCUMENTATION</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Documents We Handle</h2>
            <p className="text-gray-600 text-base max-w-2xl mx-auto">From commercial invoices to certificates of origin, we manage all export documentation requirements</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {documents.map((doc, index) => (
              <div key={index} className="p-8 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-lg transition-all duration-300 group">
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-emerald-600 transition-all">
                  <i className={`${doc.icon} text-2xl text-emerald-600 group-hover:text-white transition-all`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{doc.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{doc.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-8 bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-emerald-400 tracking-widest uppercase mb-4">OUR PROCESS</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">How We Work</h2>
            <p className="text-gray-400 text-base max-w-2xl mx-auto">A streamlined four-step process ensuring accurate and timely documentation</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-emerald-600/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-4">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.description}</p>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-8 -right-4 w-8 h-0.5 bg-emerald-600/30"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src="https://public.readdy.ai/ai/img_res/00b8aa3713c50ff79f481a3848fc628b.jpg" alt="Export Documentation" className="w-full h-[500px] object-cover object-top rounded-3xl shadow-2xl" />
            </div>
            <div>
              <p className="text-sm font-semibold text-amber-700 tracking-widest uppercase mb-4">WHY CHOOSE US</p>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Expert Documentation Support</h2>
              <p className="text-gray-600 text-base leading-relaxed mb-8">With years of experience in international trade, we understand the critical importance of accurate documentation. Our team ensures every document meets regulatory requirements and customs standards.</p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">100% Accuracy Guarantee</h4>
                    <p className="text-sm text-gray-600">Error-free documentation prepared by certified export specialists</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Fast Turnaround Time</h4>
                    <p className="text-sm text-gray-600">Quick document processing to meet your shipping deadlines</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-global-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Global Compliance</h4>
                    <p className="text-sm text-gray-600">Up-to-date knowledge of international trade regulations and requirements</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-line text-xl text-emerald-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">24/7 Support</h4>
                    <p className="text-sm text-gray-600">Round-the-clock assistance for urgent documentation needs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-8" style={{ backgroundColor: '#14B8A6' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Simplify Your Export Documentation?</h2>
          <p className="text-white/90 text-lg mb-10 max-w-2xl mx-auto">Let our experts handle the paperwork while you focus on your core business. Get started today with a free consultation.</p>
          <button className="px-10 py-4 bg-white text-emerald-600 rounded-full text-base font-semibold hover:bg-gray-100 transition-all whitespace-nowrap">
            Get Free Consultation
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
